import type { NextRouter } from 'next/router';
import type { SagaIterator } from 'redux-saga';
import { call, delay, fork, take, cancel, select, spawn, put } from 'redux-saga/effects';
import type { Client } from '@core/api/utils';
import { fetchClassTimeline } from '../api';
import type { Timeline } from '../models';
import { getTimeline, loadTimelineSuccess } from '../redux';
import { getInProgressWorkout } from '../selectors';

const EXCEEDED_END_OFFSET_POLL_MS = 1000;

export const pollVideoEndedSaga = function* (
  onVideoEndSaga: (router: NextRouter) => void,
  router: NextRouter,
  pollingDelay: number,
) {
  while (true) {
    yield call(onVideoEndSaga, router);
    yield delay(pollingDelay);
  }
};

export const pollVideoEndedSagaManager = function* (
  onVideoEndSaga: (router: NextRouter) => void,
  router: NextRouter,
  cancelActionType: string,
): SagaIterator {
  const pollTask = yield fork(
    pollVideoEndedSaga,
    onVideoEndSaga,
    router,
    EXCEEDED_END_OFFSET_POLL_MS,
  );
  yield take(cancelActionType);
  yield cancel(pollTask);
};

export const loadTimelineSaga = function* (
  client: Client,
  classId: string,
): SagaIterator {
  try {
    const klass = yield call(fetchClassTimeline, client, classId);
    yield put(loadTimelineSuccess(klass));
  } catch (e) {
    // Log
  }
};

export const pollTimelineSaga = function* (
  client: Client,
  classId: string,
  pollingDelay: number,
  cancelActionType: string,
  onVideoEndSaga?: (router: NextRouter) => void,
  router?: NextRouter,
) {
  while (true) {
    try {
      yield call(loadTimelineSaga, client, classId);
      const { videoEndOffset }: Timeline = yield select(getTimeline);

      if (videoEndOffset) {
        if (onVideoEndSaga && router) {
          yield spawn(
            pollVideoEndedSagaManager,
            onVideoEndSaga,
            router,
            cancelActionType,
          );
        }

        yield cancel();
      }
    } catch (e) {
      // swallow errors
    }

    yield delay(pollingDelay);
  }
};

export const pollTimelineSagaManager = function* (
  client: Client,
  pollingDelay: number,
  cancelActionType: string,
  onVideoEndSaga?: (router: NextRouter) => void,
  router?: NextRouter,
): SagaIterator {
  const workout: ReturnType<typeof getInProgressWorkout> = yield select(
    getInProgressWorkout,
  );
  if (!workout) {
    return;
  }
  const pollTask = yield fork(
    pollTimelineSaga,
    client,
    workout.classId,
    pollingDelay,
    cancelActionType,
    onVideoEndSaga,
    router,
  );
  yield take(cancelActionType);
  yield cancel(pollTask);
};
